/**
 * RecurrenceEditor Component
 * Allows parents to configure schedule recurrence (when schedules automatically activate).
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  TextField,
  Checkbox,
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  ScheduleRecurrence,
  RecurrenceType,
  DayOfWeek,
} from '../../types/schedule';

interface RecurrenceEditorProps {
  open: boolean;
  onClose: () => void;
  onSave: (recurrence: ScheduleRecurrence | null) => void;
  recurrence?: ScheduleRecurrence | null;
}

const ALL_DAYS: DayOfWeek[] = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

const DEFAULT_RECURRENCE: ScheduleRecurrence = {
  type: 'manual',
  daysOfWeek: null,
  startTime: null,
  endTime: null,
  startDate: null,
  endDate: null,
  notifyBeforeMinutes: null,
};

export const RecurrenceEditor: React.FC<RecurrenceEditorProps> = ({
  open,
  onClose,
  onSave,
  recurrence,
}) => {
  const { t } = useTranslation();

  // Local state
  const [type, setType] = useState<RecurrenceType>('manual');
  const [daysOfWeek, setDaysOfWeek] = useState<DayOfWeek[]>([]);
  const [startTime, setStartTime] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');
  const [notifyBefore, setNotifyBefore] = useState<number | ''>('');
  const [enableNotification, setEnableNotification] = useState(false);

  // Initialize form from props
  useEffect(() => {
    if (recurrence) {
      setType(recurrence.type);
      setDaysOfWeek(recurrence.daysOfWeek || []);
      setStartTime(recurrence.startTime || '');
      setEndTime(recurrence.endTime || '');
      setNotifyBefore(recurrence.notifyBeforeMinutes ?? '');
      setEnableNotification(recurrence.notifyBeforeMinutes != null);
    } else {
      setType('manual');
      setDaysOfWeek([]);
      setStartTime('');
      setEndTime('');
      setNotifyBefore('');
      setEnableNotification(false);
    }
  }, [recurrence, open]);

  const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newType = event.target.value as RecurrenceType;
    setType(newType);

    // Set default days for weekdays/weekends
    if (newType === 'weekdays') {
      setDaysOfWeek(['MON', 'TUE', 'WED', 'THU', 'FRI']);
    } else if (newType === 'weekends') {
      setDaysOfWeek(['SAT', 'SUN']);
    } else if (newType === 'daily') {
      setDaysOfWeek(ALL_DAYS);
    } else if (newType === 'weekly') {
      // Keep current selection for weekly
    } else {
      setDaysOfWeek([]);
    }
  };

  const handleDaysChange = (
    _event: React.MouseEvent<HTMLElement>,
    newDays: DayOfWeek[]
  ) => {
    setDaysOfWeek(newDays);
  };

  const handleSave = () => {
    if (type === 'manual') {
      onSave(null);
    } else {
      const result: ScheduleRecurrence = {
        type,
        daysOfWeek: type === 'weekly' ? daysOfWeek : null,
        startTime: startTime || null,
        endTime: endTime || null,
        startDate: null,
        endDate: null,
        notifyBeforeMinutes: enableNotification && notifyBefore !== '' ? Number(notifyBefore) : null,
      };
      onSave(result);
    }
    onClose();
  };

  const showDayPicker = type === 'weekly';
  const showTimePicker = type !== 'manual';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('schedules.recurrence.title')}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {/* Recurrence Type */}
          <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
            <FormLabel component="legend">{t('schedules.recurrence.type')}</FormLabel>
            <RadioGroup
              value={type}
              onChange={handleTypeChange}
            >
              <FormControlLabel
                value="manual"
                control={<Radio />}
                label={t('schedules.recurrence.manual')}
              />
              <FormControlLabel
                value="daily"
                control={<Radio />}
                label={t('schedules.recurrence.daily')}
              />
              <FormControlLabel
                value="weekdays"
                control={<Radio />}
                label={t('schedules.recurrence.weekdays')}
              />
              <FormControlLabel
                value="weekends"
                control={<Radio />}
                label={t('schedules.recurrence.weekends')}
              />
              <FormControlLabel
                value="weekly"
                control={<Radio />}
                label={t('schedules.recurrence.weekly')}
              />
              <FormControlLabel
                value="one_time"
                control={<Radio />}
                label={t('schedules.recurrence.one_time')}
              />
            </RadioGroup>
          </FormControl>

          {/* Day Picker (for weekly) */}
          {showDayPicker && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('schedules.recurrence.daysOfWeek')}
              </Typography>
              <ToggleButtonGroup
                value={daysOfWeek}
                onChange={handleDaysChange}
                aria-label="days of week"
                size="small"
                sx={{ flexWrap: 'wrap', gap: 0.5 }}
              >
                {ALL_DAYS.map((day) => (
                  <ToggleButton
                    key={day}
                    value={day}
                    sx={{
                      minWidth: 48,
                      '&.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'primary.dark',
                        },
                      },
                    }}
                  >
                    {t(`schedules.days.${day}`)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              {daysOfWeek.length === 0 && type === 'weekly' && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Select at least one day
                </Alert>
              )}
            </Box>
          )}

          {/* Time Pickers */}
          {showTimePicker && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label={t('schedules.recurrence.startTime')}
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label={t('schedules.recurrence.endTime')}
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  sx={{ flex: 1 }}
                />
              </Box>
            </Box>
          )}

          {/* Notification */}
          {showTimePicker && (
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={enableNotification}
                    onChange={(e) => setEnableNotification(e.target.checked)}
                  />
                }
                label={t('schedules.recurrence.notifyBefore')}
              />
              {enableNotification && (
                <TextField
                  type="number"
                  value={notifyBefore}
                  onChange={(e) => setNotifyBefore(e.target.value ? Number(e.target.value) : '')}
                  inputProps={{ min: 1, max: 60 }}
                  size="small"
                  sx={{ ml: 2, width: 100 }}
                />
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={type === 'weekly' && daysOfWeek.length === 0}
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
